import React from "react";

import {
  Card,
  CardContent,
  CardHeader,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { toPercentage } from "@/utils";
import { MediaScore } from "@/types";

import { Bubble } from "react-chartjs-2";
import { Chart as ChartJS, Legend, LinearScale, Title, Tooltip as ChartTooltip, TooltipItem, PointElement } from "chart.js";
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(
  Legend,
  Title,
  ChartTooltip,
  LinearScale,
  ChartDataLabels,
  PointElement
);

interface CustomTooltipItem extends TooltipItem<'bubble'> {
  raw: {
    x: number,
    y: number,
    name: string,
  }
}

interface ScatterCardProps {
  title: string;
  data: MediaScore[];
}

const MediaScatterCard: React.FC<ScatterCardProps> = ({
  title,
  data
}) => {
  const theme = useTheme();

  const scores = data.map((row) => (
    {
      name: row.tag_name,
      y: row.score_wt,
      x: row.rate,
      r: Math.max(row.rate * 50, 5)
    }
  ))

  return (
    <Card
      sx={{
        border: 1,
        borderRadius: 1,
        borderColor: theme.palette.divider,
      }}
    >
      <CardHeader
        title={title}
        slotProps={{ title: { variant: "body1" } }}
      />
      <CardContent
        sx={{
          pt: 0,
          pb: 2,
          "&:last-child": { pb: 2 },
          height: "300px",
        }}
      >
        <Bubble
          options={{
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                title: {
                  display: false,
                  text: 'Index',
                  color: theme.palette.text.secondary
                },
                grid: {
                  color: theme.palette.divider
                },
                ticks: {
                  color: theme.palette.text.secondary,
                  callback: function(value) {
                    if (value === 0.5) {
                      return 'None'
                    } else if (value === 1.5){
                      return 'Low'
                    } else if (value === 3.5){
                      return 'Med'
                    } else if (value === 5.5){
                      return 'High'
                    } else {
                      return undefined
                    }
                  }
                },
                border: {
                  color: theme.palette.divider
                },
              },
              x: {
                title: {
                  display: false,
                  text: 'Penetration',
                  color: theme.palette.text.secondary
                },
                grid: {
                  color: theme.palette.divider
                },
                ticks: {
                  color: theme.palette.text.secondary,
                  callback: function(value) { return toPercentage(value as number, 0)}
                },
                border: {
                  color: theme.palette.divider
                },
              }

            },

            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                displayColors: false,
                titleColor: theme.palette.info.contrastText,
                bodyColor: theme.palette.info.contrastText,
                backgroundColor: theme.palette.info.main,
                callbacks: {
                  label: function (context: CustomTooltipItem) {
                    return `${context.raw.name as String}`;
                  }
                }
              }
            }
          }}
          data={{
            datasets: [
              {
                label: 'A',
                data: scores,
                backgroundColor: theme.palette.primary.main,
                borderColor: theme.palette.primary.dark,
                datalabels: {
                  display: false
                }
              }
            ]
          }}

        />
      </CardContent>
    </Card>
  );
};

export default MediaScatterCard;
